/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

import { logger } from "@/infrastructure/lib/logger";

/**
 * A visão PESSOAL como contexto ativo da sessão (AB#85637).
 *
 * Organização e workspace já eram contextos que o seletor da barra superior
 * escolhe. O pessoal existia no modelo — catálogo próprio, nível de instalação
 * próprio — mas só se alcançava por uma aba dentro do perfil, e era o único
 * nível fora do seletor.
 *
 * Precisa de estado PRÓPRIO porque, diferente dos outros dois, ele não se
 * deduz de nada: `useViewScope` descobre organização e workspace olhando o que
 * está selecionado, e "escolhi o meu espaço" não tem onde ser lido. Sem este
 * registro, entrar na visão pessoal seria indistinguível de estar na visão da
 * organização sem workspace.
 *
 * A seleção de workspace NÃO é apagada ao entrar aqui: sair da visão pessoal
 * devolve a pessoa exatamente para onde ela estava.
 */
const STORAGE_KEY = "prodgy_personal_view";

interface PersonalViewContextType {
  /** Se a visão pessoal é o contexto ativo. */
  isPersonalView: boolean;
  /** Entra na visão pessoal. */
  enterPersonalView: () => void;
  /** Sai dela, de volta ao contexto que estava selecionado. */
  leavePersonalView: () => void;
}

const PersonalViewContext = createContext<PersonalViewContextType | undefined>(undefined);

/**
 * O armazenamento local pode estar indisponível — janela anônima, cookies
 * bloqueados —, e perder a preferência é aceitável; quebrar a aplicação por
 * causa dela não é.
 *
 * A chave é uma só, e não uma por pessoa: numa máquina compartilhada, quem
 * entrar depois começa na visão pessoal — **a dele**. Nada de ninguém vaza,
 * porque o que a tela mostra vem da sessão de quem está logado; herda-se a
 * posição do seletor, não conteúdo. Uma chave por usuário exigiria a
 * identidade aqui dentro, e isto passaria a depender do contexto de
 * autenticação só para escolher onde a barra superior abre.
 */
const lerPreferencia = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch (falha) {
    logger.warn("[PersonalView] não foi possível ler a preferência", falha);
    return false;
  }
};

const gravarPreferencia = (ativa: boolean) => {
  try {
    if (ativa) window.localStorage.setItem(STORAGE_KEY, "true");
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (falha) {
    logger.warn("[PersonalView] não foi possível gravar a preferência", falha);
  }
};

export const PersonalViewProvider = ({ children }: { children: ReactNode }) => {
  const [isPersonalView, setIsPersonalView] = useState<boolean>(lerPreferencia);

  const enterPersonalView = useCallback(() => {
    setIsPersonalView(true);
    gravarPreferencia(true);
  }, []);

  const leavePersonalView = useCallback(() => {
    setIsPersonalView(false);
    gravarPreferencia(false);
  }, []);

  return (
    <PersonalViewContext.Provider
      value={{ isPersonalView, enterPersonalView, leavePersonalView }}
    >
      {children}
    </PersonalViewContext.Provider>
  );
};

/**
 * Fora do provider a resposta é "não estou na visão pessoal", em vez de erro.
 *
 * O hook é consumido pelo `useViewScope`, que por sua vez é usado em telas
 * isoladas e em testes montados sem a árvore inteira de providers — lançar ali
 * derrubaria contextos que não têm nada a ver com esta escolha.
 */
export const usePersonalView = (): PersonalViewContextType => {
  const contexto = useContext(PersonalViewContext);

  return (
    contexto ?? {
      isPersonalView: false,
      enterPersonalView: () => {},
      leavePersonalView: () => {},
    }
  );
};
